import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useQueries } from '@tanstack/react-query'
import { Droplets, Zap, Save, ArrowLeft, Info, Pencil, Plus } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Alert, AlertDialog, Button, ButtonGroup, Card, Input, Modal, Select, Skeleton } from '@/components/ui'
import { RolloverConfirmation } from '@/components/meters/RolloverConfirmation'
import { AddMeterModal } from '@/components/meters/AddMeterModal'
import { useProperty, useProperties, useAllProperties } from '@/hooks/useProperties'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { usePropertyReadings, useCreateBulkReadings } from '@/hooks/useBulkReadings'
import type { MeterReading, PropertyReadingsData, BulkSaveResult } from '@/hooks/useBulkReadings'
import { useBulkReadingsStore, useSelectedPropertyStore } from '@/stores'
import { api } from '@/lib/api'
import { formatMonthYear } from '@/lib/date'

type MeterType = 'water' | 'electricity'

interface ConfirmRow {
  meter_id: number
  unit_number: string
  previous_value: number
  current_value: number
  usage: number
  is_edit: boolean
  is_rollover: boolean
}

/**
 * MON-14 (T149-027/029): true when a lower current reading looks like a
 * meter rollover (or a typo) rather than ordinary consumption. Mirrors the
 * server's own check (bulk_readings_controller.rb), so the badge/checkbox
 * pair appears exactly where the server would hold the row back.
 */
// eslint-disable-next-line react-refresh/only-export-components -- tested pure helper co-located with the page component
export function isRolloverReading(previous: number, current: number): boolean {
  return current < previous
}

/**
 * Mirrors Reading.rollover_usage (api/app/models/reading.rb), DISPLAY only:
 * the server is the sole source of truth for the persisted usage. `max` is
 * inferred from the previous reading's integer digit count (9985 -> 9999, a
 * 4-digit meter); usage = (max - previous) + current + 1.
 */
// eslint-disable-next-line react-refresh/only-export-components -- tested pure helper co-located with the page component
export function computeRolloverUsage(previous: number, current: number): number {
  const digitCount = String(Math.trunc(previous)).length
  const max = 10 ** digitCount - 1
  return max - previous + current + 1
}

// EP-58 T5 — condominium accounts have no per-unit meters. If a user
// direct-navigates here (bookmark, URL, etc.), render a not-applicable
// notice instead of an empty meter grid.
//
// Implemented as a top-level dispatcher because BulkReadingsImpl below
// calls 10+ hooks; an inline early return would change the hook count
// between renders (when accountSettings transitions from undefined to
// loaded) and trip React's Rules of Hooks. The dispatcher fires exactly
// one hook (useAccountSettings) before branching.
export type RoomTypeGroup = {
  roomTypeId: number
  roomTypeName: string
  roomTypeFloor: string | null
  meters: MeterReading[]
}

/**
 * Heading for a room type card: the name, then the floor when the room type
 * has one, so two same-named types read apart ("ตึก 1 · ชั้น 4" and
 * "ตึก 1 · ชั้น 5"). The floor is operator free text; one production value
 * is already written "ชั้น3", so a value that starts with the floor word in
 * either language is shown as it is rather than doubled.
 */
// eslint-disable-next-line react-refresh/only-export-components -- tested pure helper co-located with the page component
export function roomTypeHeading(
  name: string,
  floor: string | null,
  formatFloor: (floor: string) => string,
): string {
  const value = (floor ?? '').trim()
  if (!value) return name
  const alreadyLabelled = /^(ชั้น|floor)/i.test(value)
  return `${name} · ${alreadyLabelled ? value : formatFloor(value)}`
}

/**
 * Group bulk-reading rows by the room type the unit belongs to, in the order
 * the API returns them (room type name, then id, then unit number).
 *
 * Keyed on room_type_id, NOT the name: a meter belongs to a unit and a unit
 * to exactly one room type, and two room types can share a name. Production
 * property 2 names floors 4 and 5 both "ตึก 1"; grouping on the name merged
 * them into one heading, so 401 to 420 sat under the same card as 501 to
 * 520 and a stray 108.
 */
// eslint-disable-next-line react-refresh/only-export-components -- tested pure helper co-located with the page component
export function groupMetersByRoomType(meters: MeterReading[]): RoomTypeGroup[] {
  const groups: RoomTypeGroup[] = []
  const byId = new Map<number, RoomTypeGroup>()
  for (const mr of meters) {
    let group = byId.get(mr.room_type_id)
    if (!group) {
      group = {
        roomTypeId: mr.room_type_id,
        roomTypeName: mr.room_type,
        roomTypeFloor: mr.room_type_floor ?? null,
        meters: [],
      }
      byId.set(mr.room_type_id, group)
      groups.push(group)
    }
    group.meters.push(mr)
  }
  return groups
}

export function BulkReadingsPage() {
  const { data: accountSettings } = useAccountSettings()
  if (accountSettings?.account_type === 'condominium') {
    return <CondoMetersNotApplicable />
  }
  return <BulkReadingsImpl />
}

function CondoMetersNotApplicable() {
  const { t: tm } = useTranslation('meters')
  return (
    <div className="space-y-4">
      <Breadcrumbs items={[{ label: tm('title'), href: '/meter-readings' }]} />
      <PageHeader title={tm('title')} />
      <Card>
        <div className="p-6 text-center space-y-2">
          <h3 className="font-semibold">{tm('not_applicable_condo_title')}</h3>
          <p className="text-base-content/70">{tm('not_applicable_condo_body')}</p>
        </div>
      </Card>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// AllPropertiesView — rendered when showPropertyPicker && !pickedPropertyId.
// Fans out one GET /api/properties/:id/readings per property via useQueries,
// merges results, and renders them in the meter table with an added Property
// column. No backend changes required (Q3 spec).
//
// Performance note: capped at ~50 properties (controlled by useAllProperties
// which sends per_page=50). Pagination is a follow-up if accounts grow.
// ─────────────────────────────────────────────────────────────────────────────
interface AllPropertiesViewProps {
  propertyPicker: React.ReactNode
  headerSection: React.ReactNode
  meterType: MeterType
  readMonth: string
  monthOptions: { value: string; label: string }[]
  onMeterTypeChange: (type: MeterType) => void
  onMonthChange: (month: string) => void
}

function AllPropertiesView({
  propertyPicker,
  headerSection,
  meterType,
  readMonth,
  monthOptions,
  onMeterTypeChange,
  onMonthChange,
}: AllPropertiesViewProps) {
  const { t: tm } = useTranslation('meters')

  const { data: allProps, isLoading: propsLoading } = useAllProperties()
  // Wrap the `?? ||` logical expression in its own useMemo so the reference
  // is stable across renders (used as a useMemo dep twice below).
  const propertyList = useMemo(() => allProps ?? [], [allProps])

  // Fan out one query per property — same endpoint as usePropertyReadings
  const readingQueries = useQueries({
    queries: propertyList.map((prop) => ({
      queryKey: ['property-readings', prop.id, readMonth] as const,
      queryFn: async (): Promise<PropertyReadingsData> => {
        const response = await api.get<{ data: PropertyReadingsData }>(
          `/api/properties/${prop.id}/readings`,
          { params: { month: readMonth } }
        )
        return response.data.data
      },
    })),
  })

  // Hold a single skeleton until ALL queries have settled (success or error).
  const allSettled = readingQueries.every((q) => !q.isLoading)
  const isLoading = propsLoading || !allSettled

  // Collect errors (per-property, non-blocking). Log each and count them.
  const failedCount = readingQueries.filter((q) => q.isError).length
  if (failedCount > 0 && !isLoading) {
    readingQueries.forEach((q, i) => {
      if (q.isError) {
        console.error(
          `[BulkReadingsPage] Failed to load readings for property ${propertyList[i]?.name ?? i}:`,
          q.error
        )
      }
    })
  }

  // Build merged meter list annotated with property name for the extra column.
  interface AnnotatedMeter extends MeterReading {
    property_name: string
    property_id: number
  }

  const mergedMeters: AnnotatedMeter[] = useMemo(() => {
    return readingQueries.flatMap((q, i) => {
      if (!q.isSuccess || !q.data) return []
      const prop = propertyList[i]
      const meters: MeterReading[] = q.data[meterType] ?? []
      return meters.map((m) => ({
        ...m,
        property_name: prop?.name ?? String(prop?.id),
        property_id: prop?.id ?? 0,
      }))
    })
  }, [readingQueries, propertyList, meterType])

  // One panel per property in the All-Properties view — built from the FULL
  // property list (not just properties that have meters), so every property is
  // shown even when it has no meters of the selected type (it gets an empty note).
  const propertyGroups = useMemo(() => {
    return propertyList.map((prop) => ({
      property_id: prop.id,
      property_name: prop.name,
      meters: mergedMeters.filter((m) => m.property_id === prop.id),
    }))
  }, [propertyList, mergedMeters])

  return (
    <div className="space-y-6">
      {headerSection}
      {propertyPicker}

      {/* Read-only hint — All Properties view is for browsing only */}
      <Card>
        <div
          className="flex items-start gap-3 text-sm"
          data-testid="all-properties-read-only-hint"
        >
          <Info className="w-5 h-5 text-info shrink-0 mt-0.5" />
          <p className="flex-1">{tm('messages.all_properties_read_only_hint')}</p>
        </div>
      </Card>

      {/* Controls */}
      <Card>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{tm('labels.meter_type_label')}</span>
            <ButtonGroup>
              <Button
                className="join-item"
                size="sm"
                variant={meterType === 'water' ? 'primary' : 'ghost'}
                leftIcon={<Droplets className="w-4 h-4" />}
                onClick={() => onMeterTypeChange('water')}
              >
                {tm('types.water')}
              </Button>
              <Button
                className="join-item"
                size="sm"
                variant={meterType === 'electricity' ? 'primary' : 'ghost'}
                leftIcon={<Zap className="w-4 h-4" />}
                onClick={() => onMeterTypeChange('electricity')}
              >
                {tm('types.electricity')}
              </Button>
            </ButtonGroup>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{tm('labels.reading_month_label')}</span>
            <Select
              data-testid="month-picker"
              className="w-48"
              value={readMonth}
              onChange={(e) => onMonthChange(e.target.value)}
              options={monthOptions}
            />
          </div>
        </div>
      </Card>

      {/* Loading skeleton — held until ALL queries resolve */}
      {isLoading && (
        <Card>
          <Skeleton className="h-64 w-full" />
        </Card>
      )}

      {/* All-Properties view — one panel per property (property name as the panel
          header) instead of repeating it in a column. */}
      {!isLoading && propertyGroups.length > 0 && (
        <div className="space-y-4" data-testid="all-properties-meter-table">
          {propertyGroups.map((group) => (
            <Card key={group.property_id}>
              <h3 className="font-semibold mb-3">{group.property_name}</h3>
              {group.meters.length === 0 ? (
                <p className="text-sm text-base-content/50 py-2">
                  {tm('empty.no_type_meters', { type: tm(`types.${meterType}`) })}
                </p>
              ) : (
              <div className="overflow-x-auto">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>{tm('labels.unit')}</th>
                      <th>{tm('labels.previous')}</th>
                      <th>{tm('labels.current')}</th>
                      <th>{tm('labels.usage')}</th>
                      <th>{tm('labels.serial_no')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.meters.map((meter) => {
                      const previousNum = meter.latest_reading
                        ? Number(meter.latest_reading.previous_value)
                        : (meter.previous_month_value ?? 0)
                      const currentNum = meter.latest_reading
                        ? Number(meter.latest_reading.current_value)
                        : (meter.next_month_previous_value ?? previousNum)
                      const usage = currentNum - previousNum

                      return (
                        <tr key={`${meter.property_id}-${meter.meter_id}`}>
                          <td className="font-medium">{meter.unit_number}</td>
                          {/* eslint-disable-next-line no-restricted-syntax -- Meter reading, not money */}
                          <td>{previousNum.toLocaleString()}</td>
                          {/* eslint-disable-next-line no-restricted-syntax -- Meter reading, not money */}
                          <td>{currentNum.toLocaleString()}</td>
                          <td>
                            {isRolloverReading(previousNum, currentNum) ? (
                              <RolloverConfirmation usage={computeRolloverUsage(previousNum, currentNum)} />
                            ) : usage > 0 ? (
                              // eslint-disable-next-line no-restricted-syntax -- Meter consumption (kWh/m³), not money
                              <span className="text-success font-medium">+{usage.toLocaleString()}</span>
                            ) : (
                              <span className="text-base-content/40">-</span>
                            )}
                          </td>
                          <td className="text-base-content/60 text-xs">{meter.serial_number || '-'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* No properties at all — the per-property empty notes cover the
          "property has no meters of this type" case, so this only fires when
          the account has zero properties. */}
      {!isLoading && propertyGroups.length === 0 && (
        <Card>
          <p className="text-center py-8 text-base-content/60">
            {tm('empty.no_type_meters', {
              type: meterType === 'water' ? tm('types.water') : tm('types.electricity'),
            })}
          </p>
        </Card>
      )}

      {/* Per-property error footer (non-blocking) */}
      {!isLoading && failedCount > 0 && (
        <div className="text-sm text-warning text-center py-2">
          {tm('messages.partial_failures', { count: failedCount })}
        </div>
      )}
    </div>
  )
}

function BulkReadingsImpl() {
  const { t: tm, i18n } = useTranslation('meters')
  const { t: tc } = useTranslation('common')
  const { data: accountSettings } = useAccountSettings()
  const { propertyId: urlPropertyId } = useParams<{ propertyId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  // When invoked at /meter-readings (sidebar entry, no URL propertyId), the
  // selected property comes from the global header switcher (persisted in
  // localStorage). When invoked at /properties/:id/readings, the URL wins.
  const pickedPropertyId = useSelectedPropertyStore((s) => s.selectedPropertyId)
  const setPickedPropertyId = useSelectedPropertyStore((s) => s.setSelectedPropertyId)
  const propertyId = urlPropertyId ?? (pickedPropertyId ? String(pickedPropertyId) : undefined)
  const id = Number(propertyId)
  const showPropertyPicker = !urlPropertyId

  const meterType = (searchParams.get('type') as MeterType) || 'water'

  // Default to current month (YYYY-MM format)
  const [readMonth, setReadMonth] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  })

  // Scope the Zustand store by (property, month) so unsaved edits in one
  // month don't leak into another month's table when the user switches.
  const meterIdStr = propertyId ? `${propertyId}:${readMonth}` : ''
  const storeState = useBulkReadingsStore()
  const storeReadings = storeState.readings[meterIdStr]?.[meterType] ?? {}
  const storePreviousReadings = storeState.previousReadings[meterIdStr]?.[meterType] ?? {}
  const setStoreReading = useBulkReadingsStore((s) => s.setReading)
  const setStorePreviousReading = useBulkReadingsStore((s) => s.setPreviousReading)
  const resetMeterIds = useBulkReadingsStore((s) => s.resetMeterIds)

  // Build month dropdown: next month + current month + previous 10 months
  // (12 options total), ordered newest → oldest. Labels honour the account
  // calendar era + UI locale (WEB-M1) — Buddhist accounts see "มิถุนายน 2569".
  const calendar = accountSettings?.calendar ?? 'gregorian'
  const monthOptions = useMemo(() => {
    const now = new Date()
    const locale = i18n.language === 'th' ? 'th' : 'en'
    const opts: { value: string; label: string }[] = []
    for (let offset = 1; offset >= -10; offset--) {
      const d = new Date(now.getFullYear(), now.getMonth() + offset, 1)
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      const label = formatMonthYear(d, { locale, calendar })
      opts.push({ value, label })
    }
    return opts
  }, [i18n.language, calendar])

  const [showAddMeter, setShowAddMeter] = useState(false)
  const [alertDialog, setAlertDialog] = useState<{ title: string; message: string } | null>(null)
  const [confirmRows, setConfirmRows] = useState<ConfirmRow[] | null>(null)
  // Confirmation belongs to the exact property/month/type and pair of readings.
  const [rollovers, setRollovers] = useState<Record<string, { previous: number; current: number; usage: number; confirmed: boolean }>>({})
  const rolloverKey = (meterId: number) => `${meterIdStr}:${meterType}:${meterId}`
  const rolloverFor = (meterId: number, previous: number, current: number) => {
    const reviewed = rollovers[rolloverKey(meterId)]
    if (reviewed?.previous === previous && reviewed.current === current) return reviewed
    return { previous, current, usage: computeRolloverUsage(previous, current), confirmed: false }
  }
  const confirmRollover = (meterId: number, previous: number, current: number, confirmed: boolean) => {
    const rollover = rolloverFor(meterId, previous, current)
    setRollovers((state) => ({ ...state, [rolloverKey(meterId)]: { ...rollover, confirmed } }))
  }
  const clearRollover = (meterId: number) => {
    setRollovers((state) => {
      const next = { ...state }
      delete next[rolloverKey(meterId)]
      return next
    })
  }
  const [partialSummary, setPartialSummary] = useState<BulkSaveResult | null>(null)

  // No-op-only Modal auto-dismisses after 3s (per BA Q2). Locked or cascade_blocked
  // results require an explicit dismiss — keep them on screen indefinitely.
  useEffect(() => {
    if (!partialSummary) return
    const onlyNoOp =
      partialSummary.locked.length === 0 &&
      partialSummary.cascade_blocked.length === 0 &&
      partialSummary.rollover_needs_confirmation.length === 0 &&
      partialSummary.no_op.length > 0
    if (!onlyNoOp) return
    const timer = setTimeout(() => setPartialSummary(null), 3000)
    return () => clearTimeout(timer)
  }, [partialSummary])

  const { data: property, isLoading: propertyLoading } = useProperty(id)
  const { data: allReadings, isLoading: readingsLoading } = usePropertyReadings(id, readMonth)
  const { data: propertiesData } = useProperties(1)
  const propertyList = propertiesData?.data ?? []
  const createBulkReadings = useCreateBulkReadings()

  // Get current type's meters
  const currentMeters = allReadings?.[meterType] || []

  // Display values are scoped to the selected month.
  //   - latest_reading (when present) is the reading saved within this month
  //   - previous_month_value is the prior reading's current_value, used to
  //     pre-fill the Previous column when no reading exists yet (forward
  //     continuity, e.g. April→May)
  //   - next_month_previous_value is the next reading's previous_value, used
  //     to pre-fill the Current column when no reading exists yet (backward
  //     continuity, e.g. May→April)
  const serverReadings: Record<number, string> = {}
  const serverPreviousReadings: Record<number, string> = {}
  currentMeters.forEach((mr) => {
    if (!mr.meter_id) return
    if (mr.latest_reading) {
      serverReadings[mr.meter_id] = mr.latest_reading.current_value.toString()
      serverPreviousReadings[mr.meter_id] = mr.latest_reading.previous_value.toString()
    } else {
      if (mr.previous_month_value !== null && mr.previous_month_value !== undefined) {
        serverPreviousReadings[mr.meter_id] = mr.previous_month_value.toString()
      }
      if (mr.next_month_previous_value !== null && mr.next_month_previous_value !== undefined) {
        serverReadings[mr.meter_id] = mr.next_month_previous_value.toString()
      }
    }
  })

  const defaultCurrentReadings = serverReadings

  // Merge: store values override server defaults
  const readings = { ...defaultCurrentReadings, ...storeReadings }
  const previousReadings = { ...serverPreviousReadings, ...storePreviousReadings }

  const handleMeterTypeChange = (type: MeterType) => {
    setSearchParams({ type })
  }

  const handleReadingChange = (meterId: number, value: string) => {
    clearRollover(meterId)
    setStoreReading(meterIdStr, meterType, meterId, value)
  }

  const handlePreviousReadingChange = (meterId: number, value: string) => {
    clearRollover(meterId)
    setStorePreviousReading(meterIdStr, meterType, meterId, value)
  }

  // Build the bulk-save payload by diffing each meter's on-screen state
  // against its server-side latest_reading. A meter is included if EITHER
  // column was edited; both values are always sent so the server doesn't
  // have to guess (and so unchanged columns don't accidentally get clobbered
  // by the omitted-field fallback).
  const buildReadingsToSave = () => {
    const result: Array<{ meter_id: number; current_value: number; previous_value: number }> = []
    currentMeters.forEach((m) => {
      const currentInput = readings[m.meter_id]
      const previousInput = previousReadings[m.meter_id]
      // When this month has no reading yet, fall back to the continuity
      // values for the "before" baseline used in the change-detection diff.
      // Otherwise un-edited columns triggered by the continuity pre-fill
      // would falsely register as a change.
      const serverCurrent = m.latest_reading
        ? Number(m.latest_reading.current_value)
        : (m.next_month_previous_value ?? 0)
      const serverPrevious = m.latest_reading
        ? Number(m.latest_reading.previous_value)
        : (m.previous_month_value ?? 0)

      const currentNum = currentInput !== undefined && currentInput !== '' ? Number(currentInput) : serverCurrent
      const previousNum = previousInput !== undefined && previousInput !== '' ? Number(previousInput) : serverPrevious

      const changed = currentNum !== serverCurrent || previousNum !== serverPrevious
      if (!changed) return

      result.push({ meter_id: m.meter_id, current_value: currentNum, previous_value: previousNum })
    })
    return result
  }

  const handleSave = () => {
    const readingsToSave = buildReadingsToSave()

    if (readingsToSave.length === 0) {
      setAlertDialog({ title: tm('bulk.save_readings'), message: tm('messages.no_changes') })
      return
    }

    const rows: ConfirmRow[] = readingsToSave.map((r) => {
      const meter = currentMeters.find((m) => m.meter_id === r.meter_id)
      return {
        meter_id: r.meter_id,
        unit_number: meter?.unit_number ?? '-',
        previous_value: r.previous_value,
        current_value: r.current_value,
        usage: isRolloverReading(r.previous_value, r.current_value)
          ? rolloverFor(r.meter_id, r.previous_value, r.current_value).usage
          : r.current_value - r.previous_value,
        is_rollover: isRolloverReading(r.previous_value, r.current_value),
        is_edit: !!meter?.latest_reading,
      }
    })

    setConfirmRows(rows)
  }

  const handleConfirmSave = async () => {
    const readingsToSave = buildReadingsToSave().flatMap((row) => {
      if (!isRolloverReading(row.previous_value, row.current_value)) return [row]
      if (!rolloverFor(row.meter_id, row.previous_value, row.current_value).confirmed) return []
      return [{ ...row, confirmed_rollover: true }]
    })
    if (!readingsToSave.length) return

    setConfirmRows(null)

    try {
      // Anchor to billing_day of the selected month at noon. The bulk endpoint
      // overwrites the meter's latest reading in place when it isn't yet
      // referenced by any invoice, so back-dating no longer "buries" the new
      // value behind an earlier-today reading.
      const [year, month] = readMonth.split('-').map(Number)
      const billingDay = property?.billing_day || 1
      const readAtDate = new Date(year, month - 1, billingDay, 12, 0, 0)

      const response = await createBulkReadings.mutateAsync({
        propertyId: id,
        readings: readingsToSave,
        read_at: readAtDate.toISOString(),
      })

      const result = response.data
      for (const row of result.rollover_needs_confirmation) {
        const submitted = readingsToSave.find((reading) => reading.meter_id === row.meter_id)
        if (!submitted) continue
        setRollovers((state) => ({ ...state, [rolloverKey(row.meter_id)]: {
          previous: submitted.previous_value, current: submitted.current_value,
          usage: row.computed_usage, confirmed: false,
        } }))
      }
      // Never clear an unconfirmed row merely because the other rows saved.
      const savedIds = result.saved.map((row) => row.meter_id)
      if (savedIds.length) resetMeterIds(meterIdStr, meterType, savedIds)
      for (const meterId of savedIds) clearRollover(meterId)
      const allClean =
        result.locked.length === 0 &&
        result.no_op.length === 0 &&
        result.cascade_blocked.length === 0 &&
        result.rollover_needs_confirmation.length === 0

      if (allClean) {
        // Happy path — identical UX to the original all-saved flow.
        setAlertDialog({
          title: tm('bulk.save_readings'),
          message: tm('messages.readings_saved', { count: result.saved.length }),
        })
      } else {
        // Partial success — clear ONLY the saved meter IDs so locked / no_op /
        // cascade_blocked rows retain the user's input (they still need to see
        // what they typed to decide what to do next).
        setPartialSummary(result)
      }
    } catch {
      // No more invoice_locked branch — the server no longer emits that code.
      // Network/validation errors still surface here.
      setAlertDialog({ title: tm('bulk.save_readings'), message: tm('messages.save_failed') })
    }
  }

  const isLoading = propertyLoading || readingsLoading

  // In-body desktop property picker removed — property scope comes from the
  // single top-right switcher (header on desktop, PageHeader titleAction on
  // mobile). The all-properties view already prompts "select a property from
  // the top" to record/edit readings.
  const propertyPicker = null

  // Breadcrumb + header vary by mode (sidebar landing vs URL-scoped).
  const headerSection = (
    <>
      <Breadcrumbs
        items={showPropertyPicker
          ? [ { label: tc('nav.meters') } ]
          : [
              { label: tc('nav.properties'), href: '/properties' },
              { label: property?.name ?? '', href: `/properties/${id}` },
              { label: tm('meter_readings') },
            ]
        }
      />
      <PageHeader
        title={tm('meter_readings')}
        description={
          property
            ? tm('bulk_record_description', { type: tm(`types.${meterType}`), property: property.name })
            : tm('list_page.select_property')
        }
        titleAction={
          showPropertyPicker ? (
            <Select
              data-testid="page-property-picker-mobile"
              aria-label={tm('list_page.select_property')}
              value={pickedPropertyId ? String(pickedPropertyId) : ''}
              onChange={(e) => setPickedPropertyId(e.target.value ? Number(e.target.value) : null)}
              options={[
                { value: '', label: tm('list_page.all_properties') },
                ...propertyList.map((p) => ({ value: String(p.id), label: p.name })),
              ]}
              className="max-w-[9rem]"
              size="sm"
            />
          ) : undefined
        }
        primaryAction={
          property
            ? {
                key: 'save',
                label: tm('bulk.save_readings'),
                icon: Save,
                onClick: handleSave,
                loading: createBulkReadings.isPending,
              }
            : undefined
        }
        overflowActions={[
          {
            key: 'back',
            label: tc('actions.back'),
            icon: ArrowLeft,
            to: `/properties/${id}`,
            hidden: showPropertyPicker || !property,
          },
          {
            // Moved next to Save (header) from the controls card — desktop shows
            // it inline before the Save primary; mobile lists it in the ตัวเลือก sheet.
            // Shows whenever a property is loaded (both the sidebar /meter-readings
            // route and /properties/:id/readings) — unlike Back, it is NOT gated on
            // showPropertyPicker.
            key: 'add-meter',
            label: tm('add_meter_modal.open'),
            icon: Plus,
            onClick: () => setShowAddMeter(true),
            hidden: !property,
          },
        ]}
      />
    </>
  )

  // Q3: Sidebar landing with no property picked → All Properties mode.
  // Replace the old early-return guard with a real fan-out view.
  if (showPropertyPicker && !pickedPropertyId) {
    return (
      <AllPropertiesView
        propertyPicker={propertyPicker}
        headerSection={headerSection}
        meterType={meterType}
        readMonth={readMonth}
        monthOptions={monthOptions}
        onMeterTypeChange={handleMeterTypeChange}
        onMonthChange={setReadMonth}
      />
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        {headerSection}
        {propertyPicker}
        <Card>
          <Skeleton className="h-64 w-full" />
        </Card>
      </div>
    )
  }

  if (!property) {
    return (
      <div className="space-y-6">
        {headerSection}
        {propertyPicker}
        <Card>
          <p className="text-center py-8">{tc('errors.not_found')}</p>
        </Card>
      </div>
    )
  }

  const metersByRoom = groupMetersByRoomType(currentMeters)

  // Build summary for confirmation modal
  const confirmedRows = confirmRows?.filter((row) => !row.is_rollover || rolloverFor(row.meter_id, row.previous_value, row.current_value).confirmed) ?? []
  const createdCount = confirmedRows.filter((row) => !row.is_edit).length
  const editedCount = confirmedRows.filter((row) => row.is_edit).length
  // Every rollover remains reviewable, including those beyond the first five rows.
  const previewRows = confirmRows?.filter((row, index) => index < 5 || row.is_rollover) ?? []
  const extraRows = (confirmRows?.length ?? 0) - previewRows.length

  return (
    <>
      <div className="space-y-6">
        {headerSection}
        {propertyPicker}

        {/* Controls */}
        <Card>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{tm('labels.meter_type_label')}</span>
              <ButtonGroup>
                <Button
                  className="join-item"
                  size="sm"
                  variant={meterType === 'water' ? 'primary' : 'ghost'}
                  leftIcon={<Droplets className="w-4 h-4" />}
                  onClick={() => handleMeterTypeChange('water')}
                >
                  {tm('types.water')}
                </Button>
                <Button
                  className="join-item"
                  size="sm"
                  variant={meterType === 'electricity' ? 'primary' : 'ghost'}
                  leftIcon={<Zap className="w-4 h-4" />}
                  onClick={() => handleMeterTypeChange('electricity')}
                >
                  {tm('types.electricity')}
                </Button>
              </ButtonGroup>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{tm('labels.reading_month_label')}</span>
              <Select
                data-testid="month-picker"
                className="w-48"
                value={readMonth}
                onChange={(e) => setReadMonth(e.target.value)}
                options={monthOptions}
              />
              <span className="text-xs text-base-content/60">
                {tm('labels.billing_day', { day: property.billing_day || 1 })}
              </span>
            </div>
            {/* B-057: "Add meter" moved into the header actions (next to Save). */}
          </div>
        </Card>

        {/* B-057: no meters of this type for any unit — offer to add one inline
            instead of dead-ending on an empty page. */}
        {currentMeters.length === 0 && (
          <Card>
            <div className="text-center py-8 space-y-3">
              <p className="text-base-content/60">
                {tm('empty.no_type_meters', { type: tm(`types.${meterType}`) })}
              </p>
              <p className="text-sm text-base-content/50">{tm('empty.no_type_meters_cta')}</p>
              <Button
                variant="primary"
                leftIcon={<Plus className="w-4 h-4" />}
                onClick={() => setShowAddMeter(true)}
              >
                {tm('add_meter_modal.open')}
              </Button>
            </div>
          </Card>
        )}

        {/* Meters grouped by room type (by id; the name is display only) */}
        {metersByRoom.map(({ roomTypeId, roomTypeName, roomTypeFloor, meters }) => (
          <Card key={roomTypeId}>
            <h3 className="font-semibold mb-4">
              {roomTypeHeading(roomTypeName, roomTypeFloor, (floor) =>
                tm('labels.floor_value', { floor }),
              )}
            </h3>
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>{tm('labels.unit')}</th>
                    <th>{tm('labels.previous')}</th>
                    <th>{tm('labels.current')}</th>
                    <th>{tm('labels.usage')}</th>
                    <th>{tm('labels.serial_no')}</th>
                    <th aria-label={tm('messages.edit_individually')}></th>
                  </tr>
                </thead>
                <tbody>
                  {meters.map((meter) => {
                    const currentInputValue = readings[meter.meter_id] ?? ''
                    const previousInputValue =
                      previousReadings[meter.meter_id] !== undefined
                        ? previousReadings[meter.meter_id]
                        : meter.latest_reading?.previous_value?.toString()
                          ?? meter.previous_month_value?.toString()
                          ?? ''
                    const previousNum = previousInputValue !== '' ? Number(previousInputValue) : 0
                    const newValue = currentInputValue ? Number(currentInputValue) : previousNum
                    const usage = newValue - previousNum

                    return (
                      <tr key={meter.meter_id} data-meter-id={meter.meter_id}>
                        <td className="font-medium">{meter.unit_number}</td>
                        <td>
                          <Input
                            type="number"
                            className="w-28"
                            size="sm"
                            value={previousInputValue}
                            aria-label={`${meter.unit_number} ${tm('labels.previous')}`}
                            onChange={(e) => handlePreviousReadingChange(meter.meter_id, e.target.value)}
                          />
                        </td>
                        <td>
                          <Input
                            type="number"
                            className="w-28"
                            size="sm"
                            value={currentInputValue}
                            aria-label={`${meter.unit_number} ${tm('labels.current')}`}
                            onChange={(e) => handleReadingChange(meter.meter_id, e.target.value)}
                            min={0}
                          />
                        </td>
                        <td>
                          {isRolloverReading(previousNum, newValue) ? (
                            <RolloverConfirmation
                              usage={rolloverFor(meter.meter_id, previousNum, newValue).usage}
                              confirmed={rolloverFor(meter.meter_id, previousNum, newValue).confirmed}
                              onConfirm={meter.latest_reading &&
                                Number(meter.latest_reading.previous_value) === previousNum &&
                                Number(meter.latest_reading.current_value) === newValue
                                ? undefined
                                : (confirmed) => confirmRollover(meter.meter_id, previousNum, newValue, confirmed)}
                            />
                          ) : usage > 0 ? (
                            // eslint-disable-next-line no-restricted-syntax -- Meter consumption (kWh/m³), not money
                            <span className="text-success font-medium">+{usage.toLocaleString()}</span>
                          ) : (
                            <span className="text-base-content/40">-</span>
                          )}
                        </td>
                        <td className="text-base-content/60 text-xs">{meter.serial_number || '-'}</td>
                        <td>
                          {/* Per-row escape hatch to the single-meter edit page —
                              the actionable destination for the invoice_locked
                              error AND any user who wants reading history /
                              image attachments / per-reading deletes. */}
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            title={tm('messages.edit_individually')}
                            aria-label={tm('messages.edit_individually')}
                            data-testid={`edit-meter-${meter.meter_id}`}
                            onClick={() => navigate(`/meters/${meter.meter_id}`)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        ))}
        {/* No redundant empty state here — the `currentMeters.length === 0` block
            above already covers "no meters of this type" (metersByRoom is derived
            from currentMeters, so it can only be empty when currentMeters is too),
            which previously rendered a duplicate empty panel. */}
      </div>

      <AlertDialog
        isOpen={!!alertDialog}
        onClose={() => setAlertDialog(null)}
        title={alertDialog?.title ?? ''}
        message={alertDialog?.message ?? ''}
      />

      {/* Confirmation modal */}
      <Modal
        isOpen={confirmRows !== null}
        onClose={() => setConfirmRows(null)}
        title={tm('bulk.confirm_title')}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmRows(null)}>
              {tc('actions.cancel')}
            </Button>
            <Button onClick={handleConfirmSave} loading={createBulkReadings.isPending} disabled={confirmedRows.length === 0}>
              {tc('actions.confirm')}
            </Button>
          </>
        }
      >
        <p className="mb-4 text-sm">
          {tm('bulk.confirm_summary', { created: createdCount, edited: editedCount })}
        </p>
        {confirmedRows.length < (confirmRows?.length ?? 0) && (
          <Alert variant="warning" className="mb-4">{tm('rollover.unconfirmed_skipped')}</Alert>
        )}
        {previewRows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>{tm('labels.unit')}</th>
                  <th>{tm('labels.previous')}</th>
                  <th>{tm('labels.current')}</th>
                  <th>{tm('labels.usage')}</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i}>
                    <td className="font-medium">{row.unit_number}</td>
                    {/* eslint-disable-next-line no-restricted-syntax -- Meter reading, not money */}
                    <td>{row.previous_value.toLocaleString()}</td>
                    {/* eslint-disable-next-line no-restricted-syntax -- Meter reading, not money */}
                    <td>{row.current_value.toLocaleString()}</td>
                    <td>
                      {row.is_rollover ? (
                        <RolloverConfirmation
                          usage={row.usage}
                          confirmed={rolloverFor(row.meter_id, row.previous_value, row.current_value).confirmed}
                          onConfirm={(confirmed) => confirmRollover(row.meter_id, row.previous_value, row.current_value, confirmed)}
                        />
                      ) : row.usage > 0 ? (
                        // eslint-disable-next-line no-restricted-syntax -- Meter consumption (kWh/m³), not money
                        <span className="text-success font-medium">+{row.usage.toLocaleString()}</span>
                      ) : (
                        <span className="text-base-content/40">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {extraRows > 0 && (
          <p className="text-sm text-base-content/60 mt-2">
            {tm('bulk.more_rows', { n: extraRows })}
          </p>
        )}
      </Modal>

      {/* Partial-success Modal — replaces the old 422 invoice_locked AlertDialog.
          When `locked` or `cascade_blocked` rows are present, the Modal stays
          open until the user clicks Got it (no backdrop dismiss, no auto-close).
          When only no_op rows are present, the useEffect above auto-closes after
          3 seconds. */}
      <Modal
        isOpen={partialSummary !== null}
        onClose={() => setPartialSummary(null)}
        title={tm('messages.partial_save_summary_title')}
        size="lg"
        closeOnOverlay={
          !((partialSummary?.locked.length ?? 0) > 0 ||
            (partialSummary?.cascade_blocked.length ?? 0) > 0 ||
            (partialSummary?.rollover_needs_confirmation.length ?? 0) > 0)
        }
        footer={
          (partialSummary?.locked.length ?? 0) > 0 ||
          (partialSummary?.cascade_blocked.length ?? 0) > 0 ||
          (partialSummary?.rollover_needs_confirmation.length ?? 0) > 0 ? (
            <Button
              onClick={() => setPartialSummary(null)}
              data-testid="partial-save-dismiss"
            >
              {tc('actions.got_it')}
            </Button>
          ) : undefined
        }
      >
        {partialSummary && (
          <div className="space-y-4" data-testid="partial-save-modal">
            <p className="text-sm">
              {tm('messages.partial_save_summary_body', {
                saved: partialSummary.saved.length,
                locked: partialSummary.locked.length,
              })}
            </p>

            {partialSummary.rollover_needs_confirmation.length > 0 && (
              <Alert variant="warning">{tm('rollover.needs_confirmation')}</Alert>
            )}
            {partialSummary.no_op.length > 0 && (
              <Alert variant="info">
                {tm('messages.no_op_skipped', { count: partialSummary.no_op.length })}
              </Alert>
            )}

            {partialSummary.locked.length > 0 && (
              <Alert variant="warning" title={tm('messages.invoice_locked_title')}>
                <div className="overflow-x-auto mt-2">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>{tm('labels.unit')}</th>
                        <th aria-label={tm('messages.edit_individually')}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {partialSummary.locked.map((row) => (
                        <tr key={row.meter_id} data-testid="partial-save-locked-row">
                          <td className="font-medium">{row.unit_number}</td>
                          <td>
                            <Button
                              type="button"
                              variant="ghost"
                              size="xs"
                              title={tm('messages.locked_inline_edit_cta')}
                              aria-label={tm('messages.locked_inline_edit_cta')}
                              leftIcon={<Pencil className="w-3.5 h-3.5" />}
                              onClick={() => {
                                setPartialSummary(null)
                                navigate(`/meters/${row.meter_id}`)
                              }}
                            >
                              {tm('messages.locked_inline_edit_cta')}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Alert>
            )}

            {partialSummary.cascade_blocked.length > 0 && (
              <Alert variant="error">
                <div className="space-y-2">
                  <p>
                    {tm('messages.cascade_blocked_warning', {
                      count: partialSummary.cascade_blocked.length,
                    })}
                  </p>
                  <div className="overflow-x-auto">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>{tm('labels.unit')}</th>
                          <th aria-label={tm('messages.edit_individually')}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {partialSummary.cascade_blocked.map((row) => (
                          <tr key={row.meter_id} data-testid="partial-save-cascade-row">
                            <td className="font-medium">{row.unit_number}</td>
                            <td>
                              <Button
                                type="button"
                                variant="ghost"
                                size="xs"
                                title={tm('messages.locked_inline_edit_cta')}
                                aria-label={tm('messages.locked_inline_edit_cta')}
                                leftIcon={<Pencil className="w-3.5 h-3.5" />}
                                onClick={() => {
                                  setPartialSummary(null)
                                  navigate(`/meters/${row.meter_id}`)
                                }}
                              >
                                {tm('messages.locked_inline_edit_cta')}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Alert>
            )}
          </div>
        )}
      </Modal>

      <AddMeterModal
        propertyId={id}
        meterType={meterType}
        existingUnitIds={currentMeters
          .map((m) => m.unit_id)
          .filter((uid): uid is number => typeof uid === 'number')}
        isOpen={showAddMeter}
        onClose={() => setShowAddMeter(false)}
      />
    </>
  )
}
